import { SubmitHandler, useForm } from 'react-hook-form'
import { useCreateKeywordMutation, useGetClassifierDetailsQuery } from '../../api/client'
import { useNavigate, useParams } from 'react-router-dom'
import MastheadBreadcrumb from '../../components/MastheadBreadcrumb'
import PageMasthead from '../../components/PageMasthead'
import Spinner from '../../components/Spinner'
import { TextPageContainer } from '../../containers/TextPageContainer'
import { isUserAdminState } from '../../store/DomainStore'
import { useRecoilState } from 'recoil'

interface IFormInput {
    keyword: string
    regex: boolean
    allowableRegex?: string
    description?: string
    classifierDetailsId: number
}

const VALID_KEYWORD_CLASSIFIERS_MUST_CONTAIN_1 = 'keyword'
const VALID_KEYWORD_CLASSIFIERS_MUST_CONTAIN_2 = 'spam'
const VALID_KEYWORD_CLASSIFIERS_MUST_CONTAIN_3 = 'url'

const sentenceCase = (value: string | undefined) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : ''

/**
 * This page uses the react-hook-form to create a new keyword.
 * https://www.react-hook-form.com/get-started/
 */
function KeywordCreatePage() {
    let navigate = useNavigate()
    let { accountId: accountIdString } = useParams()
    const [admin] = useRecoilState(isUserAdminState)

    const accountId = Number(accountIdString)
    const keywordsSectionPath = `/account/${accountId}/settings#keywords-card`

    const [createKeywordMutation, { loading: createKeywordLoading }] = useCreateKeywordMutation()

    const { data: getClassifierDetailsData, loading: getClassifierDetailsLoading } =
        useGetClassifierDetailsQuery()

    const classifierDetails = getClassifierDetailsData?.getClassifierDetails || []

    const validKeywordClassifiers = classifierDetails?.filter(
        (classifier) =>
            classifier.displayName
                .toLowerCase()
                .indexOf(VALID_KEYWORD_CLASSIFIERS_MUST_CONTAIN_1) !== -1 ||
            // Matched on the short name rather than the display name, so that only SPAM itself qualifies. — Claude
            classifier.shortName.toLowerCase() ===
                VALID_KEYWORD_CLASSIFIERS_MUST_CONTAIN_2.toLowerCase() ||
            classifier.displayName
                .toLowerCase()
                .indexOf(VALID_KEYWORD_CLASSIFIERS_MUST_CONTAIN_3) !== -1
    )

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<IFormInput>({})

    const onSubmit: SubmitHandler<IFormInput> = async (data) => {
        await createKeywordMutation({
            variables: {
                params: {
                    keyword: data.keyword,
                    regex: data.regex,
                    allowableRegex: data.allowableRegex,
                    description: data.description,
                    accountId: accountId,
                    classifierDetailId: Number(data.classifierDetailsId)
                }
            }
        })

        navigate(keywordsSectionPath)
    }

    return (
        <>
            <PageMasthead
                title="Create a keyword"
                tabTitle="Create a keyword"
                breadcrumb={
                    <MastheadBreadcrumb to={keywordsSectionPath} label="Profile settings" />
                }
            />
            <TextPageContainer>
                <p className="text-gray-700">
                    Any content with this keyword will be moderated as severe.
                </p>
                <p className="mt-3 text-gray-700">
                    You do not need to enter keywords in different cases eg cr*p and CR*P. Instead,
                    enter just one case and Arwen will detect all cases.
                </p>

                <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                    <div className="flex flex-col ">
                        <label htmlFor="keyword" className="block  leading-6 text-gray-700 ">
                            New keyword <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                            <input
                                type="text"
                                id="keyword"
                                {...register('keyword', {
                                    required: true
                                    // minLength: 3, Left in as a comment in case we need to re-introduce some limit in future.
                                })}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-950 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600  sm:leading-6"
                            />
                        </div>
                        {errors.keyword?.type === 'required' && (
                            <p role="alert">Keyword is required</p>
                        )}

                        <label
                            htmlFor="description"
                            className="block  leading-6 text-gray-700  mt-6"
                        >
                            Description
                        </label>
                        <div className="mt-2">
                            <input
                                type="text"
                                id="description"
                                {...register('description', {
                                    required: false
                                })}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-950 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600  sm:leading-6"
                            />
                        </div>

                        <label htmlFor="classifier" className="block  leading-6 text-gray-700 mt-6">
                            Classifier <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                            <select
                                defaultValue={''}
                                id="classifier"
                                {...register('classifierDetailsId', {
                                    required: true
                                })}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-950 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600  sm:leading-6"
                            >
                                <option value="" disabled>
                                    Select the keyword type.
                                </option>
                                {validKeywordClassifiers.map((classifier) => (
                                    <option value={classifier.id!} key={classifier.id!}>
                                        {sentenceCase(classifier.displayName)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.classifierDetailsId?.type === 'required' && (
                            <p role="alert">A classifier is required</p>
                        )}

                        <div className={admin ? '' : 'hidden'}>
                            <label htmlFor="regex" className="block  leading-6 text-gray-700  mt-6">
                                Is this a regex? (Arwen Admin only)
                            </label>
                            <div className="">
                                <input
                                    type="checkbox"
                                    id="regex"
                                    {...register('regex')}
                                    className="checkbox "
                                />
                            </div>
                        </div>

                        <div className={admin ? '' : 'hidden'}>
                            <label
                                htmlFor="allowableRegex"
                                className="block  leading-6 text-gray-700  mt-6"
                            >
                                Exceptions (Arwen Admin only)
                            </label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    id="allowableRegex"
                                    {...register('allowableRegex', {
                                        required: false
                                    })}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-950 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600  sm:leading-6"
                                />
                            </div>
                        </div>

                        <div className="items-center flex flex-row mt-6">
                            {createKeywordLoading ? (
                                <div>Saving...</div>
                            ) : (
                                <input
                                    type="submit"
                                    className="text-base inline-flex items-center px-3 py-2 border border-transparent leading-4  rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-sm text-white bg-primary hover:bg-primary-700 cursor-pointer"
                                />
                            )}

                            <button
                                type="reset"
                                onClick={() => {
                                    navigate(keywordsSectionPath)
                                }}
                                className="py-2 px-6 underline"
                            >
                                Cancel
                            </button>
                            {getClassifierDetailsLoading ? (
                                <div className="ml-2">
                                    <Spinner />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </form>
            </TextPageContainer>
        </>
    )
}

export default KeywordCreatePage
